import { FilterAltOutlined } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    Divider,
    Grid,
    Autocomplete as MuiAutoComplete,
    TextField,
} from "@mui/material";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
};

type filterProps = {
    filter: any;
    setFilter: any;
    billsFetchStatus: any;
    show: boolean;
};

export const CollecteesFilter = ({
    filter,
    setFilter,
    billsFetchStatus,
    show,
}: filterProps) => {
    const columnOptions = [
        { label: "Customer Name", value: "name" },
        { label: "Meter No", value: "meterNo" },
        { label: "Connection Date", value: "connectionDate" },
        { label: "None", value: "none" },
    ];

    const orderOptions = [
        { label: "Ascending", value: "ASC" },
        { label: "Descending", value: "DESC" },
        { label: "None", value: "none" },
    ];
    const [intermediateFilter, setIntermediateFilter] = useState<any>(filter);
    const [clearAll, setClearAll] = useState(initialClearAll);

    const handleChange = (ev: ChangeEvent<any>) => {
        setIntermediateFilter((f: any) => ({
            ...f,
            [ev.target.name]: ev.target.value,
        }));
    };

    const handleFilter = (ev: FormEvent<any>) => {
        ev.preventDefault();
        setFilter((f: any) => ({
            ...intermediateFilter,
            refetchCount: f?.refetchCount ? f.refetchCount + 1 : 1,
        }));
    };

    useEffect(() => {
        if (intermediateFilter.district === "all") {
            setClearAll({
                district: true,
                village: true,
                hamlet: true,
            });
            setIntermediateFilter({
                ...intermediateFilter,
                district: "",
                village: "",
                hamlet: "",
            });
        }

        if (intermediateFilter.village === "all") {
            setClearAll({ ...clearAll, village: true, hamlet: true });
            setIntermediateFilter({
                ...intermediateFilter,
                village: "",
                hamlet: "",
            });
        }

        if (intermediateFilter.hamlet === "all") {
            setClearAll({ ...clearAll, hamlet: true });
            setIntermediateFilter({ ...intermediateFilter, hamlet: "" });
        }

        return () => {
            setClearAll({
                district: false,
                village: false,
                hamlet: false,
            });
        };
    }, [
        intermediateFilter.district,
        intermediateFilter.village,
        intermediateFilter.hamlet,
    ]);

    return (
        <Grid
            item
            container
            xs={12}
            spacing={1}
            component="form"
            onSubmit={handleFilter}
        >
            <Grid item xs={12} md={3}>
                <UpdatedSearchableInput
                    label="District"
                    api="/region/district"
                    filter={intermediateFilter}
                    setFilter={setIntermediateFilter}
                    clearAll={clearAll.district}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <UpdatedSearchableInput
                    label="Village"
                    api="/region/village"
                    filter={intermediateFilter}
                    setFilter={setIntermediateFilter}
                    dep={intermediateFilter.district}
                    params={{ district: intermediateFilter.district }}
                    clearAll={clearAll.village}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <UpdatedSearchableInput
                    label="Hamlet"
                    api="/region/hamlet"
                    filter={intermediateFilter}
                    setFilter={setIntermediateFilter}
                    dep={[
                        intermediateFilter.district,
                        intermediateFilter.village,
                    ]}
                    params={
                        intermediateFilter.village &&
                        intermediateFilter.village !== "all"
                            ? { village: [intermediateFilter.village] }
                            : { village: [0] }
                    }
                    clearAll={clearAll.hamlet}
                />
            </Grid>

            <Grid item xs={12} md={3}>
                <TextField
                    onChange={handleChange}
                    fullWidth
                    name="meterNo"
                    label="Meter no."
                    value={intermediateFilter.meterNo}
                    size="small"
                    // type="number"
                />
            </Grid>

            {show && (
                <>
                    <Grid item xs={12} sm={6} md={3}>
                        <MuiAutoComplete
                            options={columnOptions}
                            getOptionLabel={(option) => option.label}
                            onChange={(event, newValue) =>
                                setIntermediateFilter({
                                    ...intermediateFilter,
                                    selectedColumn: newValue?.value || "",
                                })
                            }
                            value={columnOptions.find(
                                (option) =>
                                    option.value ===
                                    intermediateFilter.selectedColumn
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Sort By"
                                    variant="outlined"
                                />
                            )}
                            size="small"
                            fullWidth
                        />{" "}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MuiAutoComplete
                            options={orderOptions}
                            getOptionLabel={(option) => option.label}
                            onChange={(event, newValue) =>
                                setIntermediateFilter({
                                    ...intermediateFilter,
                                    selectedOrder: newValue?.value || "",
                                })
                            }
                            value={orderOptions.find(
                                (option) =>
                                    option.value ===
                                    intermediateFilter.selectedOrder
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Order"
                                    variant="outlined"
                                />
                            )}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </>
            )}

            <Grid item xs={12} md={3}>
                <Button
                    type="submit"
                    sx={{ height: "100%" }}
                    color="secondary"
                    fullWidth
                    variant="outlined"
                    endIcon={
                        billsFetchStatus === "loading" ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <FilterAltOutlined />
                        )
                    }
                    disabled={billsFetchStatus === "loading"}
                >
                    Search
                </Button>
            </Grid>

            <Grid item xs={12} mt={2}>
                <Divider />
            </Grid>
        </Grid>
    );
};
